import { Icon, IconButton, Input, Typography } from '@smartgift/frontend-toolkit';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { MAX_FEATURE_NAME_LENGTH, RECOGNITION_CREATION_STEPS } from '@hero/constants';
import { ROUTE_PATHS } from '@hero/routes';
import { paths } from '@hero/routes/paths';
import { useUpdateRecognition } from '@hero/services';
import { useNewRecognitionStore } from '@hero/store/hooks';
import { cleanObjectEmptyFields } from '@hero/utils';

const EditableArea = styled.div`
  align-items: center;
  cursor: pointer;
  display: flex;
  gap: 16px;
  height: 100%;
  padding: 8px;
  position: absolute;

  ${Typography.S.Typography} {
    padding: 8px;
  }

  &:hover {
    ${Typography.S.Typography} {
      background-color: var(--color-bg-weakest);
      border-radius: var(--size-border-radius-sm);
    }
  }
`;

const ActionButton = styled.button`
  align-items: center;
  background: var(--color-bg-body);
  border: 1px solid var(--color-accent-secondary-weaker);
  border-radius: var(--size-border-radius-md);
  box-shadow: var(--shadow-s);
  cursor: ${({ inactive }) => (inactive ? `not-allowed` : `pointer`)};
  display: flex;
  height: 24px;
  justify-content: center;
  margin-bottom: 4px;
  margin-left: 4px;
  width: 24px;

  svg path {
    fill: ${({ inactive }) => (inactive ? `var(--color-border-default)` : '')};
  }
`;

const ActionButtons = styled.div`
  bottom: -15px;
  display: flex;
  position: absolute;
  right: -8px;
`;

const DetailItem = styled.div`
  position: absolute;
  width: 100%;
`;

const TitleWrapper = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  height: 40px;
  position: relative;
  width: 100%;

  ${Input.S.Base} {
    width: 100%;
  }
`;

const getRecognitionSteps = () => [
  {
    title: 'gifts',
    icon: <Icon name="gift-box" />,
    route: paths.NEW_RECOGNITION_GIFTS,
    routeName: RECOGNITION_CREATION_STEPS.newRecognitionGifts
  },
  {
    title: 'sending',
    icon: <Icon name="sending" />,
    route: paths.NEW_RECOGNITION_CONTACT_TYPE,
    routeName: RECOGNITION_CREATION_STEPS.newRecognitionContactType
  },
  {
    title: 'recipients',
    icon: <Icon name="contacts" />,
    route: paths.NEW_RECOGNITION_RECIPIENTS,
    routeName: RECOGNITION_CREATION_STEPS.newRecognitionRecipients
  },
  {
    title: 'experience',
    icon: <Icon name="smartphone" />,
    route: paths.NEW_RECOGNITION_EXPERIENCE,
    routeName: RECOGNITION_CREATION_STEPS.newRecognitionExperience
  }
];

function useRecognitionCreationHeader({ step, setIsPageLoading, onSave }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    addons,
    code,
    contacts,
    experienceSettings: { messageParams, branding, occasion } = {},
    settings,
    products,
    name,
    updateName,
    reset
  } = useNewRecognitionStore();
  const { mutateAsync: updateRecognition } = useUpdateRecognition();
  const recognitionSteps = getRecognitionSteps();

  const [edit, setEdit] = useState(false);
  const [recognitionName, setRecognitionName] = useState(name);
  const [inputRef, setInputRef] = useState(null);

  async function updateRecognitionWithLoading() {
    setIsPageLoading(true);
    const body = cleanObjectEmptyFields({
      addons,
      name: recognitionName,
      products,
      contacts,
      experienceSettings: { messageParams, branding, occasion },
      settings: {
        recipientVerification: settings.recipientVerification
      },
      meta: {
        creationStep: recognitionSteps[step - 1].routeName
      }
    });

    await updateRecognition({
      params: {
        recognitionCode: code
      },
      body
    });

    setIsPageLoading(false);
  }

  useEffect(() => {
    inputRef?.focus();
  }, [inputRef]);

  return {
    title: (
      <TitleWrapper>
        {!edit ? (
          <EditableArea>
            <Typography
              variant="body3"
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {name}
            </Typography>
            <IconButton
              onClick={() => {
                setEdit(true);
              }}
              variant="link"
            >
              <Icon name="edit" /> {t('edit')}
            </IconButton>
          </EditableArea>
        ) : (
          <DetailItem>
            <Input
              ref={setInputRef}
              value={recognitionName}
              variant="outlined"
              onChange={e => setRecognitionName(e.target.value)}
              error={!recognitionName}
              inputProps={{ maxLength: MAX_FEATURE_NAME_LENGTH }}
            />
            <ActionButtons>
              <ActionButton
                onClick={() => {
                  setEdit(false);
                  setRecognitionName(name);
                }}
              >
                <Icon name="close" />
              </ActionButton>
              <ActionButton
                disabled={!recognitionName}
                onClick={async () => {
                  if (name !== recognitionName) {
                    await updateRecognitionWithLoading();
                    updateName(recognitionName);
                  }
                  setEdit(false);
                }}
              >
                <Icon name="success" />
              </ActionButton>
            </ActionButtons>
          </DetailItem>
        )}
      </TitleWrapper>
    ),
    step,
    steps: recognitionSteps,
    showBorder: true,
    endButton: {
      children: t('saveAndClose'),
      onClick: async () => {
        setIsPageLoading(true);
        if (onSave) await onSave();
        await updateRecognitionWithLoading();
        navigate(ROUTE_PATHS.MY_RECOGNITIONS);
        reset();
      }
    },
    style: {
      backgroundColor: 'var(--color-bg-weakest2)'
    }
  };
}

export { useRecognitionCreationHeader };
